import React, { useEffect, useState } from 'react';
import { Form as FinalForm, FormSpy } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { LINE_ITEM_ITEM, propTypes } from '../../../util/types';
import { composeValidators, numberAtLeast, required } from '../../../util/validators';
import { PURCHASE_PROCESS_NAME } from '../../../transactions/transaction';

import {
  Form,
  FieldSelect,
  FieldTextInput,
  InlineTextButton,
  H3,
  H6,
  SecondaryButton,
} from '../../../components';

import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';

import FetchLineItemsError from '../FetchLineItemsError/FetchLineItemsError.js';

import css from './ProductOrderForm.module.css';
import { BooleanEnum } from '../../../util/enums';
import { getOfferParams } from '../../../util/genericHelpers';
import { useSelector } from 'react-redux';
import { currentUserSelector } from '../../../ducks/user.duck.js';
import { isAuthenticatedSelector } from '../../../ducks/auth.duck.js';

const parseQuantityValue = value => {
  const decimalValue =
    value && typeof value === 'object' && typeof value.toNumber === 'function'
      ? value.toNumber()
      : value;
  const parsed =
    typeof decimalValue === 'number'
      ? decimalValue
      : typeof decimalValue === 'string' && /^\d+$/.test(decimalValue.trim())
      ? Number(decimalValue)
      : NaN;
  return Number.isInteger(parsed) ? parsed : null;
};

const getLineItemsQuantity = lineItems => {
  const unitPurchase = Array.isArray(lineItems)
    ? lineItems.find(item => item.code === LINE_ITEM_ITEM && !item.reversal)
    : null;
  return parseQuantityValue(unitPurchase?.quantity || unitPurchase?.units);
};

const handleFetchLineItems = ({
  quantity,
  deliveryMethod,
  displayDeliveryMethod,
  listingId,
  isOwnListing,
  fetchLineItemsInProgress,
  onFetchTransactionLineItems,
  offerParams,
  isQuantityValid,
}) => {
  const stockReservationQuantity = parseQuantityValue(quantity);
  const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};
  const isBrowser = typeof window !== 'undefined';
  if (
    isBrowser &&
    Number.isInteger(stockReservationQuantity) &&
    isQuantityValid &&
    (!displayDeliveryMethod || deliveryMethod) &&
    !fetchLineItemsInProgress
  ) {
    onFetchTransactionLineItems({
      orderData: { stockReservationQuantity, offerParams, ...deliveryMethodMaybe },
      listingId,
      isOwnListing,
    });
  }
};

const DeliveryMethodMaybe = props => {
  const {
    displayDeliveryMethod,
    hasMultipleDeliveryMethods,
    deliveryMethod,
    hasStock,
    formId,
    intl,
  } = props;
  const showDeliveryMethodSelector = displayDeliveryMethod && hasMultipleDeliveryMethods;
  const showSingleDeliveryMethod = displayDeliveryMethod && deliveryMethod;
  return !hasStock ? null : showDeliveryMethodSelector ? (
    <FieldSelect
      id={`${formId}.deliveryMethod`}
      className={css.deliveryField}
      name="deliveryMethod"
      label={intl.formatMessage({ id: 'ProductOrderForm.deliveryMethodLabel' })}
      validate={required(intl.formatMessage({ id: 'ProductOrderForm.deliveryMethodRequired' }))}
    >
      <option disabled value="">
        {intl.formatMessage({ id: 'ProductOrderForm.selectDeliveryMethodOption' })}
      </option>
      <option value={'pickup'}>
        {intl.formatMessage({ id: 'ProductOrderForm.pickupOption' })}
      </option>
      <option value={'shipping'}>
        {intl.formatMessage({ id: 'ProductOrderForm.shippingOption' })}
      </option>
    </FieldSelect>
  ) : showSingleDeliveryMethod ? (
    <div className={css.deliveryField}>
      <H3 rootClassName={css.singleDeliveryMethodLabel}>
        {intl.formatMessage({ id: 'ProductOrderForm.deliveryMethodLabel' })}
      </H3>
      <p className={css.singleDeliveryMethodSelected}>
        {deliveryMethod === 'shipping'
          ? intl.formatMessage({ id: 'ProductOrderForm.shippingOption' })
          : intl.formatMessage({ id: 'ProductOrderForm.pickupOption' })}
      </p>
      <FieldTextInput
        id={`${formId}.deliveryMethod`}
        className={css.deliveryField}
        name="deliveryMethod"
        type="hidden"
      />
    </div>
  ) : (
    <FieldTextInput
      id={`${formId}.deliveryMethod`}
      className={css.deliveryField}
      name="deliveryMethod"
      type="hidden"
    />
  );
};

const renderForm = formRenderProps => {
  const [mounted, setMounted] = useState(false);
  const {
    // FormRenderProps from final-form
    handleSubmit,
    form: formApi,

    // Custom props passed to the form component
    intl,
    formId,
    currentStock,
    allowOrdersOfMultipleItems,
    hideQuantityControls = false,
    hasMultipleDeliveryMethods,
    displayDeliveryMethod,
    listingId,
    isOwnListing,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    price,
    payoutDetailsWarning,
    marketplaceName,
    values,
    listing,
    transaction,
    setIsOfferModalOpen,
    onQuantityChange,
    shouldHideMakeAnOfferButton,
  } = formRenderProps;

  const currentUser = useSelector(currentUserSelector);
  const isAuthenticated = useSelector(isAuthenticatedSelector);

  const offerParams = getOfferParams(transaction);
  const isAuthor = currentUser?.id?.uuid === listing?.author?.id?.uuid;
  const publicData = listing?.attributes?.publicData || {};
  const moqRaw = publicData?.moq;
  const parsedMoq = Number.isInteger(moqRaw) ? moqRaw : Number.parseInt(moqRaw, 10);
  const shouldShowQuantityControls = allowOrdersOfMultipleItems && !hideQuantityControls;
  const minimumQuantity =
    shouldShowQuantityControls && Number.isInteger(parsedMoq) && parsedMoq > 1 ? parsedMoq : 1;

  const hasUnlimitedStock = typeof currentStock !== 'number';
  const hasStockLimit = Number.isInteger(currentStock);
  const hasNoStockLeft = !hasUnlimitedStock && currentStock === 0;
  const hasInsufficientStockForMoq =
    hasStockLimit && currentStock > 0 && currentStock < minimumQuantity;
  const hasStock = hasUnlimitedStock || currentStock >= minimumQuantity;
  const hasSingleFixedQuantity = !hasUnlimitedStock && currentStock === minimumQuantity;

  const quantityRequiredMsg = intl.formatMessage({ id: 'ProductOrderForm.quantityRequired' });
  const quantityInvalidMsg = intl.formatMessage({ id: 'ProductOrderForm.quantityInvalid' });
  const quantityBelowMoqMsg = intl.formatMessage(
    { id: 'ProductOrderForm.quantityBelowMoq' },
    { moq: minimumQuantity }
  );
  const quantityAboveStockMsg = intl.formatMessage(
    { id: 'ProductOrderForm.quantityAboveStock' },
    { stock: currentStock }
  );

  const quantityRequiredValidator = required(quantityRequiredMsg);
  const quantityBaseValidator = numberAtLeast(quantityInvalidMsg, 1);
  const quantityMinimumValidator = value => {
    const parsed = parseQuantityValue(value);
    if (!Number.isInteger(parsed)) {
      return quantityInvalidMsg;
    }
    return parsed >= minimumQuantity ? undefined : quantityBelowMoqMsg;
  };
  const quantityMaximumValidator = value => {
    const parsed = parseQuantityValue(value);
    if (!Number.isInteger(parsed)) {
      return quantityInvalidMsg;
    }
    return hasStockLimit && parsed > currentStock ? quantityAboveStockMsg : undefined;
  };
  const quantityValidators = composeValidators(
    quantityRequiredValidator,
    quantityBaseValidator,
    quantityMinimumValidator,
    ...(hasStockLimit ? [quantityMaximumValidator] : [])
  );

  const selectedQuantity = parseQuantityValue(values?.quantity);
  const hasValidQuantity =
    Number.isInteger(selectedQuantity) &&
    selectedQuantity >= minimumQuantity &&
    (!hasStockLimit || selectedQuantity <= currentStock);
  const lineItemsQuantity = getLineItemsQuantity(lineItems);
  const lineItemsMatchSelection = hasValidQuantity && selectedQuantity === lineItemsQuantity;

  // Note: don't add custom logic before useEffect
  useEffect(() => {
    setMounted(true);

    // Side-effect: fetch line-items after mounting if possible
    const { quantity, deliveryMethod } = values;
    if (quantity && !hasMultipleDeliveryMethods) {
      handleFetchLineItems({
        quantity,
        deliveryMethod,
        displayDeliveryMethod,
        listingId,
        isOwnListing,
        fetchLineItemsInProgress,
        onFetchTransactionLineItems,
        offerParams,
        isQuantityValid: hasValidQuantity,
      });
    }
  }, []);

  // If form values change, update line-items for the order breakdown
  const handleOnChange = formValues => {
    const { quantity, deliveryMethod } = formValues.values;
    const changedQuantity = parseQuantityValue(quantity);
    const isChangedQuantityValid =
      Number.isInteger(changedQuantity) &&
      changedQuantity >= minimumQuantity &&
      (!hasStockLimit || changedQuantity <= currentStock);

    if (mounted) {
      handleFetchLineItems({
        quantity,
        deliveryMethod,
        displayDeliveryMethod,
        listingId,
        isOwnListing,
        fetchLineItemsInProgress,
        onFetchTransactionLineItems,
        offerParams,
        isQuantityValid: isChangedQuantityValid,
      });
    }
  };

  useEffect(() => {
    if (typeof onQuantityChange === 'function') {
      onQuantityChange(hasValidQuantity ? selectedQuantity : null);
    }
  }, [hasValidQuantity, onQuantityChange, selectedQuantity]);
  // In case quantity and deliveryMethod are missing focus on that select-input.
  // Otherwise continue with the default handleSubmit function.
  const handleFormSubmit = e => {
    const { quantity, deliveryMethod } = values || {};
    if (!hasValidQuantity) {
      e.preventDefault();
      // Blur event will show validator message
      formApi.blur('quantity');
      formApi.focus('quantity');
    } else if (displayDeliveryMethod && !deliveryMethod) {
      e.preventDefault();
      // Blur event will show validator message
      formApi.blur('deliveryMethod');
      formApi.focus('deliveryMethod');
    } else {
      handleSubmit(e);
    }
  };

  const breakdownData = {};
  const showBreakdown =
    hasValidQuantity &&
    lineItemsMatchSelection &&
    lineItems?.length > 0 &&
    !fetchLineItemsInProgress &&
    !fetchLineItemsError;

  const showContactUser = typeof onContactUser === 'function';

  const onClickContactUser = e => {
    e.preventDefault();
    onContactUser();
  };

  const contactSellerLink = (
    <InlineTextButton onClick={onClickContactUser}>
      <FormattedMessage id="ProductOrderForm.finePrintNoStockLinkText" />
    </InlineTextButton>
  );
  const submitInProgress = fetchLineItemsInProgress;
  const submitDisabled =
    !hasStock ||
    !hasValidQuantity ||
    !lineItemsMatchSelection ||
    submitInProgress ||
    !!fetchLineItemsError ||
    !lineItems?.length;

  return (
    <Form onSubmit={handleFormSubmit}>
      <FormSpy subscription={{ values: true }} onChange={handleOnChange} />
      {hasNoStockLeft || hasInsufficientStockForMoq ? null : hasSingleFixedQuantity ||
        !shouldShowQuantityControls ? (
          <FieldTextInput
            id={`${formId}.quantity`}
            className={css.quantityField}
            name="quantity"
            type="hidden"
            validate={quantityValidators}
          />
        ) : (
          <FieldTextInput
            id={`${formId}.quantity`}
            className={css.quantityField}
            name="quantity"
            type="number"
            disabled={!hasStock}
            label={intl.formatMessage({ id: 'ProductOrderForm.quantityLabel' })}
            validate={quantityValidators}
            min={minimumQuantity}
            max={hasStockLimit ? currentStock : undefined}
            step="1"
          />
        )}

      {shouldShowQuantityControls && hasStock && minimumQuantity > 1 ? (
        <p className={css.quantityHint}>
          <FormattedMessage id="ProductOrderForm.minimumOrderHint" values={{ moq: minimumQuantity }} />
        </p>
      ) : null}
      {!hideQuantityControls && hasStockLimit && hasStock ? (
        <p className={css.quantityHint}>
          <FormattedMessage id="ProductOrderForm.availableStockHint" values={{ stock: currentStock }} />
        </p>
      ) : null}
      {!isAuthenticated ? (
        <p className={css.priceVisibilityHint}>
          <FormattedMessage id="ProductOrderForm.signInToViewPricing" />
        </p>
      ) : null}
      {hasInsufficientStockForMoq ? (
        <p className={css.error}>
          <FormattedMessage
            id="ProductOrderForm.quantityUnavailableForMoq"
            values={{ moq: minimumQuantity, stock: currentStock }}
          />
        </p>
      ) : null}

      <DeliveryMethodMaybe
        displayDeliveryMethod={displayDeliveryMethod}
        hasMultipleDeliveryMethods={hasMultipleDeliveryMethods}
        deliveryMethod={values?.deliveryMethod}
        hasStock={hasStock}
        formId={formId}
        intl={intl}
      />

      {showBreakdown ? (
        <div className={css.breakdownWrapper}>
          <H6 as="h3" className={css.bookingBreakdownTitle}>
            <FormattedMessage id="ProductOrderForm.breakdownTitle" />
          </H6>
          <hr className={css.totalDivider} />
          <EstimatedCustomerBreakdownMaybe
            breakdownData={breakdownData}
            lineItems={lineItems}
            currency={price.currency}
            marketplaceName={marketplaceName}
            processName={PURCHASE_PROCESS_NAME}
          />
        </div>
      ) : null}

      <FetchLineItemsError error={fetchLineItemsError} />

      <div className={css.buttonWrapper}>
        <SecondaryButton
          type="submit"
          inProgress={submitInProgress}
          disabled={submitDisabled}
          className={css.submitButton}
        >
          {hasStock ? (
            <FormattedMessage id="ProductOrderForm.ctaButton" />
          ) : (
            <FormattedMessage id="ProductOrderForm.ctaButtonNoStock" />
          )}
        </SecondaryButton>
        {!isAuthor && !shouldHideMakeAnOfferButton ? (
          <SecondaryButton
            className={css.makeOfferButton}
            type="button"
            onClick={() => setIsOfferModalOpen(BooleanEnum.TRUE)}
          >
            <FormattedMessage id="ProductOrderForm.makeAnOffer" />
          </SecondaryButton>
        ) : null}
      </div>
      <p className={css.finePrint}>
        {payoutDetailsWarning ? (
          payoutDetailsWarning
        ) : hasStock && isOwnListing ? (
          <FormattedMessage id="ProductOrderForm.ownListing" />
        ) : hasStock ? (
          <FormattedMessage id="ProductOrderForm.finePrint" />
        ) : showContactUser ? (
          <FormattedMessage id="ProductOrderForm.finePrintNoStock" values={{ contactSellerLink }} />
        ) : null}
      </p>
    </Form>
  );
};

/**
 * A form for ordering a product.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} props.marketplaceName - The name of the marketplace
 * @param {string} props.formId - The ID of the form
 * @param {Function} props.onSubmit - The function to handle the form submission
 * @param {propTypes.uuid} props.listingId - The ID of the listing
 * @param {propTypes.money} props.price - The price of the listing
 * @param {number} props.currentStock - The current stock of the listing
 * @param {boolean} props.isOwnListing - Whether the listing is owned by the current user
 * @param {boolean} props.pickupEnabled - Whether pickup is enabled
 * @param {boolean} props.shippingEnabled - Whether shipping is enabled
 * @param {boolean} props.displayDeliveryMethod - Whether the delivery method is displayed
 * @param {Object} props.lineItems - The line items
 * @param {Function} props.onFetchTransactionLineItems - The function to fetch the transaction line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the line items are being fetched
 * @param {propTypes.error} props.fetchLineItemsError - The error for fetching the line items
 * @param {Function} props.onContactUser - The function to contact the user
 * @returns {JSX.Element}
 */
const ProductOrderForm = props => {
  const intl = useIntl();
  const {
    price,
    currentStock,
    pickupEnabled,
    shippingEnabled,
    displayDeliveryMethod,
    allowOrdersOfMultipleItems,
    hideQuantityControls = false,
    listing,
  } = props;

  // Should not happen for listings that go through EditListingWizard.
  // However, this might happen for imported listings.
  if (displayDeliveryMethod && !pickupEnabled && !shippingEnabled) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProductOrderForm.noDeliveryMethodSet" />
      </p>
    );
  }

  const moqRaw = listing?.attributes?.publicData?.moq;
  const parsedMoq = Number.isInteger(moqRaw) ? moqRaw : Number.parseInt(moqRaw, 10);
  const shouldShowQuantityControls = allowOrdersOfMultipleItems && !hideQuantityControls;
  const minimumQuantity =
    shouldShowQuantityControls && Number.isInteger(parsedMoq) && parsedMoq > 1 ? parsedMoq : 1;
  const hasUnlimitedStock = typeof currentStock !== 'number';
  const hasPurchasableQuantity = hasUnlimitedStock || currentStock >= minimumQuantity;
  const hasOneItemMode = !shouldShowQuantityControls;
  const quantityMaybe = hasPurchasableQuantity
    ? { quantity: String(hasOneItemMode ? 1 : minimumQuantity) }
    : {};
  const deliveryMethodMaybe =
    shippingEnabled && !pickupEnabled
      ? { deliveryMethod: 'shipping' }
      : !shippingEnabled && pickupEnabled
        ? { deliveryMethod: 'pickup' }
        : !shippingEnabled && !pickupEnabled
          ? { deliveryMethod: 'none' }
          : {};
  const hasMultipleDeliveryMethods = pickupEnabled && shippingEnabled;
  const initialValues = { ...quantityMaybe, ...deliveryMethodMaybe };

  return (
    <FinalForm
      initialValues={initialValues}
      hasMultipleDeliveryMethods={hasMultipleDeliveryMethods}
      displayDeliveryMethod={displayDeliveryMethod}
      {...props}
      intl={intl}
      render={renderForm}
    />
  );
};

export default ProductOrderForm;
